import React, {FC, ReactNode} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router-dom';
import background from '../../assets/background.png';
import {Box, Grid, Link, styled, Typography} from '@mui/material';
import routes from '../../routes';
import theme from '../../theme';
import {useAuth} from '../../contexts/AuthenticationContext';
import {useTranslation} from 'react-i18next';
import {LogoHeader} from "../../components/LogoHeader/LogoHeader";

const PageContainer = styled(Grid)(({ theme }) => ({
    ...theme.typography.body2,
    height: '100vh',
    backgroundImage: `url(${background})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    boxShadow: 'inset 0 0 0 1000px rgba(0,0,0,.4)'
}));

const LeftPart = styled(Grid)({
    backgroundColor: '#F8F9F8',
    padding: '2vh 4vw 3vh 4vw',
});

const ContentRow = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    height: '80vh',
    paddingTop: '5vh',
    [theme.breakpoints.between('xs', 'sm')]: {height: '85vh', paddingTop: '2vh'},
    [theme.breakpoints.up('xl')]: {paddingTop: '10vh'}
}));

const RexplorerCredentialsRow = styled(Grid)(({ theme }) => ({
    ...theme.typography.body2,
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    height: '5vh',
    [theme.breakpoints.between('xs', 'sm')]: {height: '0vh'},
}));

interface HeaderProps {
    size: 'small' | 'large';
}

const Header = styled(Typography)(({ size: size }: HeaderProps) => ({
    fontSize: size === 'small' ? '18px' : '36px',
    color: '#FFFFFF',
    fontWeight: '500',
    justifyContent: 'center',
    lineHeight: 'normal',
    paddingBottom: '3vh',
    [theme.breakpoints.down('md')]: {display: 'none'}
}));

interface BaseLayoutProps {
    children?: ReactNode;
}

const BaseLayout: FC<BaseLayoutProps> = ({ children }) => {
    let location = useLocation();
    const { isAuthenticated } = useAuth();
    const { t } = useTranslation();

    const headings = {
        'discover': t('discover_solar_potential'),
        'login': t('sign_up_or_log_in'),
        'ask': t('continue_discovering'),
        'start': t('get_started'),
    };

    if (isAuthenticated()) {
        return <Navigate to={routes.APP.ROOT} />;
    }

    function renderRexplorerCredentials() {
        return (
            <Typography align="center" p={1} sx={{
                p: 0, [theme.breakpoints.between('xs', 'sm')]: {fontSize: '0px'}
            }}>
                {t('built_by_rexplorer_1')} {
                <Link href="https://www.rexplorer.ee/" target="_blank"
                      sx={{fontSize: '16px', [theme.breakpoints.between('xs', 'sm')]: {fontSize: '0px'}}}
                >
                    {t('built_by_rexplorer_2')}
                </Link>
            } {`${t('built_by_rexplorer_3')} ❤️ 🇪🇪`}
            </Typography>
        );
    }

    function getHeadings(heading: string, subHeading?: string) {
        return (
            <Grid item md={9} display="flex" justifyContent="center" flexDirection="column" alignSelf="center" pb={'10vh'}>
                <Header size="large" align="center">{heading}</Header>
                {subHeading && <Header size="small" align="center">{subHeading}</Header>}
            </Grid>
        );
    }

    return (
        <PageContainer container>
            <LeftPart item md={6} width="100%">
                <LogoHeader />
                <ContentRow container>
                    <Grid item xs={12} lg={10}>
                        {children || <Outlet />}
                    </Grid>
                </ContentRow>
                <RexplorerCredentialsRow>
                    {renderRexplorerCredentials()}
                </RexplorerCredentialsRow>
            </LeftPart>
            <Grid item md={6} width="100%" display="flex" justifyContent="center" alignItems="center">
                <Box pb={10} display="flex" justifyContent="center" alignItems="center">
                    {location.pathname === '/explore' && getHeadings(headings.discover)}
                    {location.pathname === '/result' && getHeadings(headings.login, headings.ask)}
                    {location.pathname === '/register' && getHeadings(headings.discover,headings.start)}
                    {(location.pathname === '/login' || location.pathname === '/') && getHeadings(headings.login, headings.ask)}
                </Box>
            </Grid>
        </PageContainer>
    );
};

export default BaseLayout;
